<template>
  <div class="fire-modal-overlay" @click.self="$emit('close')">
    <div class="fire-modal">
      <div class="fire-modal__header">
        <div class="fire-modal__title">
          Ручной запуск события
          <span class="fire-modal__node">{{ instanceNode?.bpmn_node_id ? `узел #${instanceNode.bpmn_node_id}` : '' }}</span>
        </div>
        <button class="fire-modal__close" @click="$emit('close')">✕</button>
      </div>

      <div class="fire-modal__body">
        <!-- Выбор события из доступных рёбер -->
        <div v-if="availableEdges.length" class="fire-modal__field">
          <label class="fire-modal__label">Событие (из схемы)</label>
          <div class="fire-modal__presets">
            <button
              v-for="(edge, i) in availableEdges"
              :key="i"
              :class="['fire-modal__preset', { 'fire-modal__preset--active': selectedPreset === i }]"
              @click="applyPreset(edge, i)"
            >
              <span class="fire-modal__preset-event">{{ edge.trigger_event }}</span>
              <span v-if="edge.trigger_doc_type_code" class="fire-modal__preset-doc">{{ edge.trigger_doc_type_code }}</span>
              <span v-if="edge.trigger_assign_type_code" class="fire-modal__preset-role">{{ edge.trigger_assign_type_code }}</span>
              <span class="fire-modal__preset-arrow">→ {{ edge.target_label }}</span>
            </button>
          </div>
        </div>
        <div v-else class="fire-modal__no-edges">
          Нет рёбер в схеме — введите параметры вручную
        </div>

        <!-- Поля ввода -->
        <div class="fire-modal__fields">
          <div class="fire-modal__field">
            <label class="fire-modal__label">event_type <span class="fire-modal__required">*</span></label>
            <input v-model="form.event_type" class="fire-modal__input" placeholder="напр. DOC_SIGNED" />
          </div>
          <div class="fire-modal__field">
            <label class="fire-modal__label">doc_type_code</label>
            <input v-model="form.doc_type_code" class="fire-modal__input" placeholder="напр. ASR_PRE_CALL" />
          </div>
          <div class="fire-modal__field">
            <label class="fire-modal__label">assign_type_code</label>
            <input v-model="form.assign_type_code" class="fire-modal__input" placeholder="напр. CONTRACTOR" />
          </div>
          <div class="fire-modal__field">
            <label class="fire-modal__label">task_type_code</label>
            <input v-model="form.task_type_code" class="fire-modal__input" placeholder="необязательно" />
          </div>
        </div>

        <!-- Результат -->
        <div v-if="result" :class="['fire-modal__result', result.success ? 'fire-modal__result--ok' : 'fire-modal__result--error']">
          <span v-if="result.success">✓ Событие успешно обработано</span>
          <span v-else>⚠ {{ result.error }}</span>
        </div>
      </div>

      <div class="fire-modal__footer">
        <button class="fire-modal__btn-cancel" @click="$emit('close')">Отмена</button>
        <button
          class="fire-modal__btn-run"
          :disabled="!form.event_type || loading"
          @click="run"
        >
          <span v-if="loading">Выполняется...</span>
          <span v-else>▶ Выполнить</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { wcheckBpmnAPI } from './services/api'

const props = defineProps({
  instanceNode: { type: Object, required: true },
  processInstanceId: { type: Number, required: true },
  schemaEdges: { type: Array, default: () => [] },
})

const emit = defineEmits(['close', 'success'])

const form = ref({ event_type: '', doc_type_code: '', assign_type_code: '', task_type_code: '' })
const loading = ref(false)
const result = ref(null)
const selectedPreset = ref(null)

// Рёбра, исходящие из данного узла
const availableEdges = computed(() =>
  props.schemaEdges
    .filter((e) => e.source_node_id === props.instanceNode.bpmn_node_id && e.trigger_event)
    .map((e) => ({
      trigger_event: e.trigger_event,
      trigger_doc_type_code: e.trigger_doc_type_code || '',
      trigger_assign_type_code: e.trigger_assign_type_code || '',
      trigger_task_type_code: e.trigger_task_type_code || '',
      target_label: e.target_label || String(e.target_node_id),
    }))
)

// Предзаполнить из первого ребра если есть только одно
watch(availableEdges, (edges) => {
  if (edges.length === 1) applyPreset(edges[0], 0)
}, { immediate: true })

function applyPreset(edge, idx) {
  selectedPreset.value = idx
  form.value.event_type = edge.trigger_event
  form.value.doc_type_code = edge.trigger_doc_type_code
  form.value.assign_type_code = edge.trigger_assign_type_code
  form.value.task_type_code = edge.trigger_task_type_code
  result.value = null
}

async function run() {
  if (!form.value.event_type) return
  loading.value = true
  result.value = null
  try {
    const res = await wcheckBpmnAPI.fireEvent({
      process_instance_id: props.processInstanceId,
      event_type: form.value.event_type || null,
      doc_type_code: form.value.doc_type_code || null,
      assign_type_code: form.value.assign_type_code || null,
      task_type_code: form.value.task_type_code || null,
    })
    if (res?.success) {
      result.value = { success: true }
      setTimeout(() => emit('success'), 1200)
    } else {
      result.value = { success: false, error: res?.error || 'Неизвестная ошибка' }
    }
  } catch (e) {
    result.value = { success: false, error: e?.response?.data?.error || e?.message || 'Ошибка запроса' }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.fire-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.fire-modal {
  background: #fff;
  border-radius: 12px;
  width: 480px;
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
  display: flex;
  flex-direction: column;
}

.fire-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
  border-bottom: 1px solid #e0e0e0;
}

.fire-modal__title {
  font-size: 15px;
  font-weight: 700;
  color: #212121;
  display: flex;
  align-items: center;
  gap: 8px;
}

.fire-modal__node {
  font-size: 12px;
  font-weight: 400;
  color: #757575;
}

.fire-modal__close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: #9e9e9e;
  padding: 2px 6px;
}
.fire-modal__close:hover { color: #424242; }

.fire-modal__body {
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.fire-modal__label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #546e7a;
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.fire-modal__required {
  color: #c62828;
}

.fire-modal__presets {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fire-modal__preset {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  padding: 7px 10px;
  border: 1.5px solid #e0e0e0;
  border-radius: 6px;
  background: #fafafa;
  cursor: pointer;
  text-align: left;
  font-size: 12px;
  transition: all 0.15s;
}
.fire-modal__preset:hover { border-color: #1976d2; background: #e3f2fd; }
.fire-modal__preset--active { border-color: #1976d2; background: #e3f2fd; }

.fire-modal__preset-event {
  font-weight: 700;
  color: #212121;
}
.fire-modal__preset-doc {
  background: #e8f5e9;
  color: #2e7d32;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 11px;
}
.fire-modal__preset-role {
  background: #e3f2fd;
  color: #1565c0;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 11px;
}
.fire-modal__preset-arrow {
  color: #9e9e9e;
  font-size: 11px;
  margin-left: auto;
}

.fire-modal__no-edges {
  font-size: 12px;
  color: #9e9e9e;
  font-style: italic;
}

.fire-modal__fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.fire-modal__input {
  width: 100%;
  padding: 7px 10px;
  border: 1.5px solid #e0e0e0;
  border-radius: 6px;
  font-size: 13px;
  outline: none;
  box-sizing: border-box;
}
.fire-modal__input:focus { border-color: #1976d2; }

.fire-modal__result {
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
}
.fire-modal__result--ok {
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
}
.fire-modal__result--error {
  background: #ffebee;
  color: #c62828;
  border: 1px solid #ef9a9a;
  word-break: break-word;
}

.fire-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px 16px;
  border-top: 1px solid #e0e0e0;
}

.fire-modal__btn-cancel {
  padding: 8px 18px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
  color: #546e7a;
}
.fire-modal__btn-cancel:hover { background: #f5f5f5; }

.fire-modal__btn-run {
  padding: 8px 20px;
  border: none;
  border-radius: 6px;
  background: #1976d2;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.fire-modal__btn-run:hover:not(:disabled) { background: #1565c0; }
.fire-modal__btn-run:disabled { background: #b0bec5; cursor: not-allowed; }
</style>
